import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import Header from './components/Header';
import AppSidebar from './components/AppSidebar';
import { shouldShowAppSidebar, isInternalPortalPath } from './constants/appNavigation';
import { legacyCarbonSavingsTradingTarget } from './constants/carbonSavingsTrading';
import { MobileNavProvider, useMobileNav } from './context/MobileNavContext';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import PreLoginDashboard from './components/PreLoginDashboard';
import { SUBSCRIPTION_PLANS_ENABLED } from './constants/subscriptionPlans';
import PricingRouteGate from './components/PricingRouteGate';
import Dashboard from './components/Dashboard';
import {
  BillingCancellationPolicyPage,
  BillingPrivacyPolicyPage,
  BillingRefundPolicyPage,
  BillingTermsAndConditionsPage,
} from './components/billing/BillingLegalPages';
import ProtectedRoute from './components/ProtectedRoute';
import RequireMSME from './components/RequireMSME';
import RequireExportIntent from './components/RequireExportIntent';
import RequireWorkflowPlanner from './components/RequireWorkflowPlanner';
import RequireMyCompanyPages from './components/RequireMyCompanyPages';
import UserLogin from './components/UserLogin';
import { RegistrationProvider, useRegistration } from './context/RegistrationContext';
import { OrganizationProvider } from './context/OrganizationContext';
import UserRegister from './components/UserRegister';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminRoute from './components/admin/AdminRoute';
import AdminWriteRoute from './components/admin/AdminWriteRoute';
import SupportRoute from './components/support/SupportRoute';
import ExpertRoute from './components/expert/ExpertRoute';
import PartnerRoute from './components/partner/PartnerRoute';
import NotFoundPage from './components/NotFoundPage';
import NavigationRouteProgress from './components/NavigationRouteProgress';
import RouteLoadingGate from './components/RouteLoadingGate';
import UpgradePromptBanner from './components/UpgradePromptBanner';
import PageInformationBanner from './components/PageInformationBanner';
import MsmeMobileBottomNav, { MSME_MOBILE_BOTTOM_NAV_HEIGHT } from './components/MsmeMobileBottomNav';
import AiAdvisorLauncher, {
  AI_ADVISOR_FAB_BOTTOM_DESKTOP_PX,
  AI_ADVISOR_FAB_GAP_PX,
  AI_ADVISOR_FAB_SIZE_PX,
} from './components/aiAdvisor/AiAdvisorLauncher';
import {
  APP_CONTENT_GUTTER,
  desktopAdvisorScrollPadding,
  hiddenShellScrollbarSx,
  mobileWorkspaceScrollPadding,
} from './constants/appShellLayout';
import { resolveDocumentTitle } from './utils/routePageMeta';
import { isBillUploadEnabled } from './constants/uiVisibility';
import { loadBillUploadSettings, useBillUploadSettings } from './hooks/useBillUploadSettings';
import { hasClientAuthSession } from './utils/sessionAuth';

const namedPage = (
  loader: () => Promise<Record<string, React.ComponentType<any>>>,
  name: string
) => lazyWithRetry(async () => {
  const loaded = await loader();
  return { default: loaded[name] };
});

const LegacyCarbonSavingsTradingRedirect: React.FC = () => {
  const location = useLocation();
  const target = legacyCarbonSavingsTradingTarget(location.pathname, location.search)
    || '/carbon-savings?section=savings';
  return <Navigate to={target} replace />;
};

const MSMERegistration = lazyWithRetry(() => import('./components/MSMERegistration'));
const CarbonSavingsAndTrading = lazyWithRetry(() => import('./components/CarbonSavingsAndTrading'));
const ComplianceHubHome = lazyWithRetry(() => import('./components/compliance/ComplianceHubHome'));
const IndiaComplianceTrack = lazyWithRetry(() => import('./components/compliance/IndiaComplianceTrack'));
const ExportComplianceTrack = lazyWithRetry(() => import('./components/compliance/ExportComplianceTrack'));
const ProductPcfWorkspace = lazyWithRetry(() => import('./components/compliance/ProductPcfWorkspace'));
const GeotaggedEvidenceCapturePage = lazyWithRetry(() => import('./components/compliance/GeotaggedEvidenceCapturePage'));
const PublicCarbonPassportPage = lazyWithRetry(() => import('./components/compliance/PublicCarbonPassportPage'));
const SupplierInvitePage = lazyWithRetry(() => import('./components/compliance/SupplierInvitePage'));
const DataPrivacy = lazyWithRetry(() => import('./components/DataPrivacy'));
const BillingPage = lazyWithRetry(() => import('./components/BillingPage'));
const BillingUnavailablePage = lazyWithRetry(() => import('./components/BillingUnavailablePage'));
const EnterpriseAppRoutes = lazyWithRetry(() => import('./components/enterprise/EnterpriseAppRoutes'));
const MSMEProfileSection = lazyWithRetry(() => import('./components/MSMEProfileSection'));
const InformationPage = lazyWithRetry(() => import('./components/InformationPage'));
const AboutUsPage = lazyWithRetry(() => import('./components/AboutUsPage'));
const InsightsPage = lazyWithRetry(() => import('./components/InsightsPage'));
const InsightArticlePage = lazyWithRetry(() => import('./components/InsightArticlePage'));
const WhyCarbonIntelligencePage = lazyWithRetry(() => import('./components/WhyCarbonIntelligencePage'));
const HelpCenterPage = namedPage(() => import('./components/StaticPages'), 'HelpCenterPage');
const PrivacyPolicyPage = namedPage(() => import('./components/StaticPages'), 'PrivacyPolicyPage');
const SettingsPage = namedPage(() => import('./components/StaticPages'), 'SettingsPage');
const TermsOfServicePage = namedPage(() => import('./components/StaticPages'), 'TermsOfServicePage');
const AdminDashboard = lazyWithRetry(() => import('./components/admin/AdminDashboard'));
const AdminSystemHealthDashboard = lazyWithRetry(() => import('./components/admin/AdminSystemHealthDashboard'));
const EmissionFactorReviewAdmin = lazyWithRetry(() => import('./components/admin/EmissionFactorReviewAdmin'));
const MSMEDetailView = lazyWithRetry(() => import('./components/admin/MSMEDetailView'));
const BuyerAuditWarRoom = lazyWithRetry(() => import('./components/compliance/BuyerAuditWarRoom'));
const SupportDashboard = lazyWithRetry(() => import('./components/support/SupportDashboard'));
const ExpertDashboard = lazyWithRetry(() => import('./components/expert/ExpertDashboard'));
const SupportMSMEView = lazyWithRetry(() => import('./components/support/SupportMSMEView'));
const PartnerIntegrationsAdmin = lazyWithRetry(() => import('./components/admin/PartnerIntegrationsAdmin'));
const BlogArticlesAdmin = lazyWithRetry(() => import('./components/admin/BlogArticlesAdmin'));
const BenchmarkReports = lazyWithRetry(() => import('./components/BenchmarkReports'));
const AutomatedTestingDashboard = lazyWithRetry(() => import('./components/AutomatedTestingDashboard'));
const PartnersPage = lazyWithRetry(() => import('./components/partners/PartnersPage'));
const PartnerApiDocumentationPage = lazyWithRetry(() => import('./components/partners/PartnerApiDocumentationPage'));
const PartnerDashboard = lazyWithRetry(() => import('./components/partner/PartnerDashboard'));
const CarbonForecastingGraph = lazyWithRetry(() => import('./components/CarbonForecastingGraph'));
const CarbonFootprint = lazyWithRetry(() => import('./components/CarbonFootprint'));
const ReportingScreen = lazyWithRetry(() => import('./components/ReportingScreen'));
const DocumentManagement = lazyWithRetry(() => import('./components/DocumentManagement'));
const OperationsPage = lazyWithRetry(() => import('./components/OperationsPage'));
const CarbonTwinPage = lazyWithRetry(() => import('./components/carbonTwin/CarbonTwinPage'));

interface RouteWrapperProps {
  children: React.ReactElement;
}

const OnboardedRoute: React.FC<RouteWrapperProps> = ({ children }) => (
  <ProtectedRoute>
    <RequireMSME>
      <RequireWorkflowPlanner>
        <RequireMyCompanyPages>{children}</RequireMyCompanyPages>
      </RequireWorkflowPlanner>
    </RequireMSME>
  </ProtectedRoute>
);

interface BillUploadRouteProps {
  enabled: React.ReactElement;
  disabled: React.ReactElement;
}

/**
 * Picks the bill-upload or fallback element only once the admin's upload settings have loaded,
 * so a hard load of /document-upload is not redirected by the build-time default first.
 */
const BillUploadRoute: React.FC<BillUploadRouteProps> = ({ enabled, disabled }) => {
  const settings = useBillUploadSettings();
  const [settled, setSettled] = useState(() => settings !== null || !hasClientAuthSession());

  useEffect(() => {
    if (settled) {
      return undefined;
    }
    let active = true;
    void loadBillUploadSettings().finally(() => {
      if (active) setSettled(true);
    });
    return () => {
      active = false;
    };
  }, [settled]);

  if (!settings && !settled) {
    return <RouteLoadingGate message="Loading page..." minHeight="40vh" />;
  }
  return isBillUploadEnabled() ? enabled : disabled;
};

const RequireMSMEProfile: React.FC<RouteWrapperProps> = ({ children }) => (
  <ProtectedRoute>
    <RequireMSME>{children}</RequireMSME>
  </ProtectedRoute>
);

function AppRoutes() {
  const location = useLocation();
  const { isRegistered, isLoggingOut } = useRegistration();
  const { closeMobileNav } = useMobileNav();
  const showSidebar = !isLoggingOut && shouldShowAppSidebar(location.pathname, isRegistered);
  const hideGlobalChrome = isInternalPortalPath(location.pathname);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  // Admin-controlled bill upload switch; re-renders the app (nav, upload screens) when it changes.
  useBillUploadSettings(location.pathname);

  useEffect(() => {
    closeMobileNav();
  }, [closeMobileNav, location.pathname, location.search]);

  useEffect(() => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
    if (typeof window.scrollTo !== 'function') {
      return;
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.title = resolveDocumentTitle(location.pathname || '/');
  }, [location.pathname]);

  useEffect(() => {
    if (!isRegistered) {
      return undefined;
    }
    // Dashboard APIs finish first. These chunks are the slow first visits.
    const id = window.setTimeout(() => {
      void import('./components/ReportingScreen');
      void import('./components/CarbonFootprint');
      void import('./components/DocumentManagement');
      void import('./components/OperationsPage');
      void import('./components/carbonTwin/CarbonTwinPage');
    }, 6000);
    return () => window.clearTimeout(id);
  }, [isRegistered]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100dvh',
        maxHeight: '100dvh',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <NavigationRouteProgress />
      {!hideGlobalChrome ? (
        <Header
          desktopSidebarCollapsed={desktopSidebarCollapsed}
          onExpandDesktopSidebar={() => setDesktopSidebarCollapsed(false)}
        />
      ) : null}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {showSidebar && (
          <AppSidebar
            desktopCollapsed={desktopSidebarCollapsed}
            onCollapseDesktopSidebar={() => setDesktopSidebarCollapsed(true)}
          />
        )}

        <Container
          ref={mainScrollRef}
          disableGutters
          maxWidth={false}
          sx={{
            px: APP_CONTENT_GUTTER,
            pt: { xs: 2.5, md: 3 },
            // Keep the last content row clear of the fixed bottom nav and AI Advisor button.
            pb: {
              xs: showSidebar
                ? mobileWorkspaceScrollPadding(
                  MSME_MOBILE_BOTTOM_NAV_HEIGHT,
                  AI_ADVISOR_FAB_SIZE_PX,
                  AI_ADVISOR_FAB_GAP_PX,
                )
                : 2.5,
              md: showSidebar && !hideGlobalChrome
                ? desktopAdvisorScrollPadding(
                  AI_ADVISOR_FAB_SIZE_PX,
                  AI_ADVISOR_FAB_BOTTOM_DESKTOP_PX,
                  AI_ADVISOR_FAB_GAP_PX,
                )
                : 4,
            },
            // Fill the flex main column beside the sidebar. No pixel maxWidth: Chrome zoom
            // < 100% expands the CSS viewport, and caps like 1600/1920 left a large empty band.
            // Readable form line-length uses FORM_STACK_MAX_WIDTH on form stacks, not the shell.
            width: '100%',
            maxWidth: 'none',
            ml: 0,
            mr: 0,
            alignSelf: 'stretch',
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            ...hiddenShellScrollbarSx,
          }}
        >
          <Box
            id="main-content"
            component="main"
            tabIndex={-1}
            sx={{
              pb: { xs: 1, md: 3 },
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
              {showSidebar && isRegistered && !hideGlobalChrome && !isLoggingOut ? <UpgradePromptBanner /> : null}
              {!hideGlobalChrome && !isLoggingOut ? <PageInformationBanner /> : null}
              <ErrorBoundary resetKeys={[location.pathname, location.search]}>
              <Suspense fallback={<RouteLoadingGate message="Loading page..." minHeight="40vh" />}>
              <Routes>
              <Route
                path="/msme-registration"
                element={(
                  <ProtectedRoute>
                    <MSMERegistration />
                  </ProtectedRoute>
                )}
              />
              <Route path="/" element={<PreLoginDashboard />} />
              <Route path="/pricing" element={<PricingRouteGate />} />
              <Route path="/login" element={<UserLogin />} />
              <Route path="/register" element={<UserRegister />} />
              <Route path="/enterprise-program" element={<Navigate to="/enterprise" replace />} />
              <Route path="/enterprise/*" element={<EnterpriseAppRoutes />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/insights/:slug" element={<InsightArticlePage />} />
              <Route path="/blog-articles" element={<Navigate to="/insights" replace />} />
              <Route path="/blog-articles/:slug" element={<Navigate to="/insights" replace />} />
              <Route path="/why-carbon-intelligence" element={<WhyCarbonIntelligencePage />} />
              <Route path="/why-sci" element={<Navigate to="/why-carbon-intelligence" replace />} />
              <Route path="/why-ci" element={<Navigate to="/why-carbon-intelligence" replace />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/carbon-passport" element={<PublicCarbonPassportPage />} />
              <Route path="/carbon-passport/:token" element={<PublicCarbonPassportPage />} />
              <Route path="/supplier-invite/:token" element={<SupplierInvitePage />} />
              <Route path="/partners/api-documentation" element={<PartnerApiDocumentationPage />} />
              <Route path="/developers" element={<Navigate to="/partners" replace />} />
              <Route path="/partner" element={<Navigate to="/login" replace />} />
              <Route path="/partner/login" element={<Navigate to="/login" replace />} />
              <Route
                path="/partner/dashboard"
                element={(
                  <PartnerRoute>
                    <PartnerDashboard />
                  </PartnerRoute>
                )}
              />
              <Route path="/admin" element={<Navigate to="/login" replace />} />
              <Route path="/admin/login" element={<Navigate to="/login" replace />} />
              <Route
                path="/admin/dashboard"
                element={(
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/msme/:id"
                element={(
                  <AdminRoute>
                    <MSMEDetailView />
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/integrations"
                element={(
                  <AdminRoute>
                    <PartnerIntegrationsAdmin />
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/blog-articles"
                element={(
                  <AdminRoute>
                    <BlogArticlesAdmin />
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/ocr-benchmarks"
                element={(
                  <AdminRoute>
                    <AdminWriteRoute>
                      <BenchmarkReports />
                    </AdminWriteRoute>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/ocr-automation"
                element={(
                  <AdminRoute>
                    <AdminWriteRoute>
                      <AutomatedTestingDashboard />
                    </AdminWriteRoute>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/emission-factors"
                element={(
                  <AdminRoute>
                    <AdminWriteRoute>
                      <EmissionFactorReviewAdmin />
                    </AdminWriteRoute>
                  </AdminRoute>
                )}
              />
              <Route
                path="/admin/system-health"
                element={(
                  <AdminRoute>
                    <AdminWriteRoute>
                      <AdminSystemHealthDashboard />
                    </AdminWriteRoute>
                  </AdminRoute>
                )}
              />
              <Route
                path="/support/dashboard"
                element={(
                  <SupportRoute>
                    <SupportDashboard />
                  </SupportRoute>
                )}
              />
              <Route
                path="/support/msme/:id"
                element={(
                  <SupportRoute>
                    <SupportMSMEView />
                  </SupportRoute>
                )}
              />
              <Route path="/support" element={<Navigate to="/support/dashboard" replace />} />
              <Route
                path="/expert/dashboard"
                element={(
                  <ExpertRoute>
                    <ExpertDashboard />
                  </ExpertRoute>
                )}
              />
              <Route path="/expert" element={<Navigate to="/expert/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={(
                  <OnboardedRoute>
                    <Dashboard />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/dashboard/*"
                element={(
                  <OnboardedRoute>
                    <NotFoundPage />
                  </OnboardedRoute>
                )}
              />
              <Route path="/msme-list" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/msme-profile/workflow-planner"
                element={<Navigate to="/operations" replace />}
              />
              <Route
                path="/msme-profile/*"
                element={(
                  <RequireMSMEProfile>
                    <MSMEProfileSection />
                  </RequireMSMEProfile>
                )}
              />
              <Route
                path="/operations/*"
                element={(
                  <OnboardedRoute>
                    <OperationsPage />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/carbon-twin"
                element={(
                  <OnboardedRoute>
                    <CarbonTwinPage />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/carbon-footprint"
                element={(
                  <OnboardedRoute>
                    <CarbonFootprint />
                  </OnboardedRoute>
                )}
              />
              <Route path="/carbon-marketplace" element={<LegacyCarbonSavingsTradingRedirect />} />
              <Route path="/carbon-credits" element={<LegacyCarbonSavingsTradingRedirect />} />
              <Route path="/carbon-trading" element={<LegacyCarbonSavingsTradingRedirect />} />
              <Route
                path="/carbon-savings"
                element={(
                  <OnboardedRoute>
                    <CarbonSavingsAndTrading />
                  </OnboardedRoute>
                )}
              />
              <Route path="/gift-schemes" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/finance" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/adeetie" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/gift-applications" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/gift-applications/*" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route
                path="/recommendations"
                element={<Navigate to="/carbon-footprint?section=recommendations" replace />}
              />
              <Route
                path="/incentives"
                element={<Navigate to="/carbon-savings?section=schemes" replace />}
              />
              <Route
                path="/compliance"
                element={(
                  <OnboardedRoute>
                    <ComplianceHubHome />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/compliance/india"
                element={(
                  <OnboardedRoute>
                    <IndiaComplianceTrack />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/buyer-audit"
                element={(
                  <OnboardedRoute>
                    <RequireExportIntent>
                      <BuyerAuditWarRoom />
                    </RequireExportIntent>
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/compliance/export"
                element={(
                  <OnboardedRoute>
                    <RequireExportIntent>
                      <ExportComplianceTrack />
                    </RequireExportIntent>
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/compliance/product-pcf"
                element={(
                  <OnboardedRoute>
                    <ProductPcfWorkspace />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/compliance/geotagged-capture"
                element={(
                  <OnboardedRoute>
                    <GeotaggedEvidenceCapturePage />
                  </OnboardedRoute>
                )}
              />
              <Route path="/reports" element={<Navigate to="/reporting" replace />} />
              <Route
                path="/reporting"
                element={(
                  <OnboardedRoute>
                    <ReportingScreen />
                  </OnboardedRoute>
                )}
              />
              <Route path="/green-loans" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route
                path="/carbon-forecasting"
                element={(
                  <OnboardedRoute>
                    <CarbonForecastingGraph />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/ai-advisor"
                element={(
                  <OnboardedRoute>
                    <Navigate to="/dashboard?advisor=1" replace />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/carbon-copilot"
                element={<Navigate to="/dashboard?advisor=1" replace />}
              />
              <Route
                path="/emissions-reduction-targets"
                element={<LegacyCarbonSavingsTradingRedirect />}
              />
              <Route
                path="/documents"
                element={(
                  <BillUploadRoute
                    enabled={<Navigate to="/document-upload" replace />}
                    disabled={<Navigate to="/data?section=connectors" replace />}
                  />
                )}
              />
              <Route
                path="/document-upload"
                element={(
                  <BillUploadRoute
                    enabled={(
                      <OnboardedRoute>
                        <DocumentManagement />
                      </OnboardedRoute>
                    )}
                    disabled={<Navigate to="/data?section=connectors" replace />}
                  />
                )}
              />
              <Route
                path="/data"
                element={(
                  <OnboardedRoute>
                    <DocumentManagement />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/document-management"
                element={(
                  <Navigate to="/data" replace />
                )}
              />
              <Route
                path="/data-privacy"
                element={(
                  <OnboardedRoute>
                    <DataPrivacy />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/billing"
                element={(
                  <OnboardedRoute>
                    {SUBSCRIPTION_PLANS_ENABLED ? <BillingPage /> : <BillingUnavailablePage />}
                  </OnboardedRoute>
                )}
              />
              <Route path="/billing/terms-and-conditions" element={<BillingTermsAndConditionsPage />} />
              <Route path="/billing/privacy-policy" element={<BillingPrivacyPolicyPage />} />
              <Route path="/billing/refund-policy" element={<BillingRefundPolicyPage />} />
              <Route path="/billing/cancellation-policy" element={<BillingCancellationPolicyPage />} />
              <Route
                path="/manufacturing-workflow"
                element={(
                  <Navigate to="/operations" replace />
                )}
              />
              <Route
                path="/settings"
                element={(
                  <OnboardedRoute>
                    <SettingsPage />
                  </OnboardedRoute>
                )}
              />
              <Route
                path="/information"
                element={(
                  <OnboardedRoute>
                    <InformationPage />
                  </OnboardedRoute>
                )}
              />
                
              {/* QA aliases / legacy deep links (CI-BUG-037/047/048) */}
              <Route path="/compliance-hub/india" element={<Navigate to="/compliance/india" replace />} />
              <Route path="/compliance-hub" element={<Navigate to="/compliance" replace />} />
              <Route path="/green-finance/*" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/green-finance" element={<Navigate to="/carbon-savings?section=schemes" replace />} />
              <Route path="/transactions" element={<Navigate to="/carbon-footprint?section=operational" replace />} />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              </ErrorBoundary>
          </Box>
        </Container>
      </Box>
      {showSidebar && !hideGlobalChrome && !isLoggingOut ? <AiAdvisorLauncher /> : null}
      <MsmeMobileBottomNav />
    </Box>
  );
}

function App() {
  return (
    <RegistrationProvider>
      <OrganizationProvider>
        <MobileNavProvider>
          <AppRoutes />
        </MobileNavProvider>
      </OrganizationProvider>
    </RegistrationProvider>
  );
}

export default App;
